/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/rules-of-hooks */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import shallowEqual from 'react-utils/shallowEqual';
import { OpenPopupContext } from '../../context/internal/OpenPopupContext';
import SyntheticEvent from '../../core/SyntheticEvent';
import { makePubSubChannel, usePubSubChannel, usePublish, useSubscribe } from '../../hooks/pubSub';
import useFactory from '../../hooks/useFactory';
import { elementIsOnScreen, getIframeAwareSurroundingWindows, getIframeAwareAncestorElements, handleTabWithOrdering } from '../../utils/Dom';
import UIAbstractFloaty from '../UIAbstractFloaty';
import { makeFloatyLayerElement } from '../utils/Layer';
import { computePopupPosition, findBestPosition, getAttachment, getRectWithoutWhitespace } from '../utils/Placement';
/** Channel for announcing position events to other popups that may need to respond. */

const interPopupChannel = makePubSubChannel();
/**
 * `UIPopoverFloaty` builds on `UIAbstractFloaty`, adding `UIPopover`-specific features:
 * - Calculates position based on the `placement` prop
 * - Overrides `placement` to avoid viewport boundaries if `autoPlacement` is enabled
 * - Tells the popover arrow where to render itself to point at the target
 * - Tells any nested popovers when they need to reposition themselves
 * - Inserts its content into the document tab order
 */

const UIPopoverFloaty = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    arrowWidth,
    autoPlacement,
    content,
    distance,
    inset,
    onFocusLeave,
    onPlacementChange,
    pinToConstraint,
    pinToOutOfBoundsTarget,
    placement,
    rootNode,
    targetElement,
    zIndex
  } = props;
  /** ref to the UIAbstractFloaty */

  const floatyRef = ref || useRef();
  /** The ID used for the layer element */

  const uniqueId = useUniqueId(`uiopenpopup-`); // The `positionConfig` incorporates all internal state from auto-placement and pinning

  const [positionConfig, setPositionConfig] = useState({
    placement
  });
  /** Ref to the current value of `positionConfig` */

  const positionConfigRef = useRef();
  positionConfigRef.current = positionConfig;
  /** Ref to the previous value of the `placement` prop */

  const prevPlacementRef = useRef(placement); // Reset `positionConfig` if we receive a new `placement` prop value

  if (prevPlacementRef.current !== placement) {
    prevPlacementRef.current = placement;
    setPositionConfig({
      placement
    });
  }
  /** Establish a channel to tell descendants when we've repositioned */


  const contextChannel = usePubSubChannel();
  const publishToContext = usePublish(contextChannel);
  /** Also be ready to tell other popups when we've repositioned */

  const publishToPeers = usePublish(interPopupChannel);
  /** The DOM element where the content will be rendered */

  const layerElement = useFactory(() => {
    return makeFloatyLayerElement(rootNode, uniqueId, zIndex);
  });
  /** Ref that tracks whether the focus is inside of the popup content */

  const hasFocusRef = useRef(false);
  const {
    placement: currentPlacement,
    pinned,
    topPinning,
    leftPinning
  } = positionConfigRef.current;
  const pinning = pinned && {
    top: topPinning,
    left: leftPinning
  };
  let popoverDimensions;
  let targetRect;
  let computedPosition;
  /** Recompute the position of the popup */

  const computePosition = ({
    contentDimensions
  }) => {
    popoverDimensions = contentDimensions;
    targetRect = getRectWithoutWhitespace(targetElement);
    const offsetParent = layerElement.offsetParent;
    computedPosition = computePopupPosition(currentPlacement, popoverDimensions, targetRect, arrowWidth, distance, inset, offsetParent, pinning);
    return computedPosition.rect;
  };
  /** After the DOM update that results from computePosition(), perform follow-up tasks */


  const onPositionChange = () => {
    layerElement.className = layerElement.className.replace(/private-popover-attached-\w+/g, '');
    const [attachmentY, attachmentX] = getAttachment(currentPlacement).split(' ');
    layerElement.classList.add(`private-popover-attached-${attachmentY}`);
    layerElement.classList.add(`private-popover-attached-${attachmentX}`); // Update subscribers with the new position

    publishToContext(Object.assign({}, computedPosition, {
      pinning,
      placement: currentPlacement
    })); // Update peers so that they know our layer element has moved

    publishToPeers(layerElement);

    if (autoPlacement) {
      const isPinnable = pinToOutOfBoundsTarget || elementIsOnScreen(targetElement);
      const newPositionConfig = findBestPosition(currentPlacement, autoPlacement, isPinnable && pinToConstraint, popoverDimensions, targetRect, arrowWidth, distance, inset);
      /*
        If the current targetElement is not in the DOM anymore, don't try to position accordingly.
        Otherwise, it'll result in an all-0 rect and detect a false collision. (#9667)
      */

      if (targetElement && getIframeAwareAncestorElements(targetElement).length === 0) return;

      if (!shallowEqual(positionConfigRef.current, newPositionConfig)) {
        setPositionConfig(newPositionConfig);
      }

      if (newPositionConfig.placement !== currentPlacement) {
        onPlacementChange(SyntheticEvent(newPositionConfig.placement));
      }
    }
  };
  /** Reposition if our target is in another popover that's moved */


  useSubscribe(interPopupChannel, repositionedLayerElement => {
    if (!(floatyRef && floatyRef.current)) return;

    if (repositionedLayerElement.contains(targetElement)) {
      floatyRef.current.reposition();
    }
  }); // Event handlers (bound on mount and whenever `targetElement` changes)

  useLayoutEffect(() => {
    /** Listen for "Tab" key presses, to allow the user to tab in and out of the popup */
    const handleWindowKeyDown = evt => {
      handleTabWithOrdering(evt, targetElement, layerElement);
    };
    /** Fire `onFocusLeave()` if the keyboard focus has left the popup content */


    const handleWindowFocus = () => {
      const newHasFocus = layerElement.contains(document.activeElement);
      if (hasFocusRef.current && !newHasFocus) onFocusLeave();
      hasFocusRef.current = newHasFocus;
    };

    const surroundingWindows = getIframeAwareSurroundingWindows(targetElement);
    surroundingWindows.forEach(surroundingWindow => {
      surroundingWindow.addEventListener('keydown', handleWindowKeyDown);
      if (onFocusLeave) surroundingWindow.addEventListener('focus', handleWindowFocus, true);
    });
    return () => {
      surroundingWindows.forEach(surroundingWindow => {
        try {
          surroundingWindow.removeEventListener('keydown', handleWindowKeyDown);
        } catch (err) {
          return;
        }

        if (onFocusLeave) surroundingWindow.removeEventListener('focus', handleWindowFocus, true);
      });
    };
  }, [targetElement]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue = useMemo(() => ({
    positionChannel: contextChannel
  }), [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => () => {
    if (layerElement && layerElement.parentElement) {
      layerElement.parentElement.removeChild(layerElement);
    }
  }, [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  return /*#__PURE__*/_jsx(OpenPopupContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(UIAbstractFloaty, {
      computePosition: computePosition,
      content: content,
      _hideContentWhenMissingTarget: false,
      layer: layerElement,
      onPositionChange: onPositionChange,
      target: targetElement,
      ref: floatyRef,
      zIndex: zIndex
    })
  });
});
UIPopoverFloaty.displayName = 'UIPopoverFloaty';
export default UIPopoverFloaty;